import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ClipboardList, MapPin, Lock, Unlock, CheckCircle2, Send, Zap, Phone, Wallet } from 'lucide-react';

const VendorBuyLeadsTab = ({ buyLeads, onRespond, onUnlock, credits = 0, onRecharge }) => {
  const [active, setActive] = useState(null); // the lead being responded to
  const [message, setMessage] = useState('');
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmLead, setConfirmLead] = useState(null); // lead pending unlock confirmation
  const [unlocking, setUnlocking] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);

  const openRespond = (lead) => {
    setActive(lead);
    setMessage(lead.my_response || '');
    setPrice('');
  };

  const startUnlock = (lead) => {
    if (credits < 1) { setShowRecharge(true); return; }
    setConfirmLead(lead);
  };

  const doUnlock = async () => {
    if (!confirmLead) return;
    setUnlocking(true);
    const res = await onUnlock(confirmLead.id);
    setUnlocking(false);
    setConfirmLead(null);
    if (res?.insufficient) setShowRecharge(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    const ok = await onRespond(active.id, { message: message.trim(), price: price.trim() });
    setSubmitting(false);
    if (ok) { setActive(null); setMessage(''); setPrice(''); }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-lg font-bold">Buy Leads</h2>
          <span className="text-xs text-gray-500">Buyer requirements matched to your category · 1 credit unlocks full buyer contact</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border ${credits > 0 ? 'bg-blue-50 text-[#2c54b2] border-blue-200' : 'bg-red-50 text-red-600 border-red-200'}`}
            data-testid="buy-leads-credit-balance"
          >
            <Zap className="w-4 h-4" /> {credits} credit{credits === 1 ? '' : 's'}
          </span>
          <Button size="sm" variant="outline" onClick={onRecharge} className="h-8 text-xs" data-testid="buy-leads-recharge-btn">
            <Wallet className="w-3.5 h-3.5 mr-1.5" /> Recharge
          </Button>
        </div>
      </div>

      {(!buyLeads || buyLeads.length === 0) ? (
        <div className="bg-white border border-gray-200 rounded-md p-10 text-center text-gray-500" data-testid="buy-leads-empty">
          <ClipboardList className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          No buy leads yet. When buyers post a requirement in your category, it&apos;ll appear here.
        </div>
      ) : (
        <div className="grid gap-3" data-testid="buy-leads-list">
          {buyLeads.map((l) => (
            <div key={l.id} className="bg-white border border-gray-200 rounded-md p-4" data-testid={`buy-lead-${l.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{l.product}</h3>
                    {l.category_name && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-[#2c54b2] border border-blue-100">{l.category_name}</span>
                    )}
                    {l.unlocked && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full" data-testid={`buy-lead-unlocked-${l.id}`}>
                        <Unlock className="w-3 h-3" /> Unlocked
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {(l.quantity || l.unit) && <span>Qty: <b>{l.quantity || '—'} {l.unit}</b></span>}
                    {l.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {l.city}</span>}
                    <span className="text-xs text-gray-400">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                {l.responded ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full" data-testid={`buy-lead-responded-${l.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Responded
                  </span>
                ) : null}
              </div>

              {l.detail && <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{l.detail}</p>}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <span className="font-medium text-gray-700">{l.buyer_name}</span>
                  {l.unlocked && l.buyer_mobile ? (
                    <a
                      href={`tel:+91${l.buyer_mobile}`}
                      className="inline-flex items-center gap-1 text-green-700 font-semibold hover:underline"
                      data-testid={`buy-lead-mobile-${l.id}`}
                    >
                      <Phone className="w-3 h-3" /> +91 {l.buyer_mobile}
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-400"><Lock className="w-3 h-3" /> +91 {l.buyer_mobile_masked}</span>
                  )}
                </div>
                {l.unlocked ? (
                  <Button
                    size="sm"
                    onClick={() => openRespond(l)}
                    className="bg-[#ee5e23] hover:bg-[#d24e15] text-white text-xs h-8"
                    data-testid={`buy-lead-respond-btn-${l.id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" /> {l.responded ? 'Update Response' : 'Respond'}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => startUnlock(l)}
                    className="bg-[#2c54b2] hover:bg-[#1e3a8a] text-white text-xs h-8"
                    data-testid={`buy-lead-unlock-btn-${l.id}`}
                  >
                    <Unlock className="w-3.5 h-3.5 mr-1.5" /> Unlock Lead · 1 credit
                  </Button>
                )}
              </div>

              {l.responded && l.my_response && (
                <div className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded p-2">
                  <span className="font-medium text-gray-700">Your response: </span>{l.my_response}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Unlock confirmation */}
      <Dialog open={!!confirmLead} onOpenChange={(o) => !o && setConfirmLead(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Unlock className="w-5 h-5 text-[#2c54b2]" /> Unlock this lead?</DialogTitle>
          </DialogHeader>
          {confirmLead && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                <b>{confirmLead.product}</b>{confirmLead.quantity ? ` — ${confirmLead.quantity} ${confirmLead.unit}` : ''}{confirmLead.city ? ` • ${confirmLead.city}` : ''}
              </div>
              <p className="text-sm text-gray-600">
                1 credit will be deducted to reveal the buyer&apos;s full name and mobile number, and let you send a quote.
              </p>
              <div className="text-xs text-gray-500 flex items-center justify-between bg-blue-50 border border-blue-100 rounded p-2">
                <span>Balance after unlock</span>
                <span className="font-bold text-[#2c54b2]">{credits} → {credits - 1} credits</span>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setConfirmLead(null)} data-testid="unlock-cancel-btn">Cancel</Button>
                <Button
                  type="button"
                  disabled={unlocking}
                  onClick={doUnlock}
                  className="flex-1 bg-[#2c54b2] hover:bg-[#1e3a8a] text-white"
                  data-testid="unlock-confirm-btn"
                >
                  {unlocking ? 'Unlocking…' : 'Use 1 credit'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Insufficient balance → recharge */}
      <Dialog open={showRecharge} onOpenChange={setShowRecharge}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Wallet className="w-5 h-5" /> Insufficient credit balance</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="recharge-prompt">
            <p className="text-sm text-gray-600">
              You need at least <b>1 credit</b> to unlock a buy lead. Your current balance is <b>{credits}</b>.
              Recharge your account to keep buying leads — credits never expire.
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowRecharge(false)} data-testid="recharge-cancel-btn">Later</Button>
              <Button
                type="button"
                className="flex-1 bg-[#ee5e23] hover:bg-[#d24e15] text-white"
                onClick={() => { setShowRecharge(false); onRecharge?.(); }}
                data-testid="recharge-now-btn"
              >
                <Wallet className="w-4 h-4 mr-1.5" /> Recharge Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Respond dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Respond to buyer requirement</DialogTitle>
          </DialogHeader>
          {active && (
            <form onSubmit={submit} className="space-y-3">
              <div className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                <b>{active.product}</b>{active.quantity ? ` — ${active.quantity} ${active.unit}` : ''}{active.city ? ` • ${active.city}` : ''}
              </div>
              <div>
                <Label className="text-xs">Your quote / message *</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  placeholder="e.g., We can supply this. Best price, ready stock, delivery in 3 days. Call us to discuss."
                  data-testid="buy-lead-respond-message"
                />
              </div>
              <div>
                <Label className="text-xs">Price (optional)</Label>
                <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g., ₹320 / piece" data-testid="buy-lead-respond-price" />
              </div>
              <p className="text-[11px] text-gray-400">You have the buyer&apos;s direct number — you can also call them right away.</p>
              <Button type="submit" disabled={submitting || !message.trim()} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="buy-lead-respond-submit">
                {submitting ? 'Sending...' : 'Send Response'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VendorBuyLeadsTab;
